import {
    Autocomplete,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { useTheme } from '@mui/system'
import React, { useEffect, useState } from 'react'
import CustomTextField from '../../components/common/CustomTextField'
import { EventDto } from '../../services/api/sharedTypes/Event'
import { ClientLookupDto } from '../../services/api/sharedTypes/Client'
import { ClientManager } from '../../services/api/ClientManager'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { convertOrderStateTypeToString, convertStringToOrderState, convertStringToPaymentState } from '../../utils/event-utils'
import { OrderState, PaymentState } from '../../services/api/sharedTypes/Common'
import DateTimeRangePicker from '../../components/common/DateTimeRangePicker'
import PaymentSelect from '../../components/common/PaymentSelect'
import CreateInvoice from '../../components/event/invoice/CreateInvoice'
import { InvoiceDto, InvoicePaymentType } from '../../services/api/sharedTypes/Invoice'
import CustomContainer from '../../components/common/CustomContainer'
import EventContact from './EventContact'
import { convertToSearchString } from '../../utils/global-utils'
import SmallPlusButton from '../common/SmallPlusButton'
import CheckBoxContainer from '../common/CheckBoxContainer'

interface EventBasicInformationProps {
    event: EventDto
    setEvent: (event: EventDto) => void
    invoice: InvoiceDto | undefined
    createInvoice?: (
        invoiceIssuerId: string,
        productsText: string,
        orderNumber: string,
        paymentType: InvoicePaymentType,
        issueDate?: Date,
        taxDate?: Date,
        dueDate?: Date
    ) => void
    onDeleteInvoice?: (invoiceId: string) => void
    onAddNewClient: () => void
    recalculateOperatingTimeForEachProduct?: () => void
    addHoursToEvent?: number // used to add hours to end time when event start is changed
}

const EventBasicInformation: React.FC<EventBasicInformationProps> = ({
    event,
    setEvent,
    invoice,
    createInvoice,
    onDeleteInvoice,
    onAddNewClient,
    recalculateOperatingTimeForEachProduct,
    addHoursToEvent,
}: EventBasicInformationProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [clients, setClients] = useState<ClientLookupDto[]>()

    const [loadingData, setLoadingData] = useState<boolean>(true)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingData(true)
                const clientRes = await ClientManager.getManager().getLookupClientsAsync()
                if (!clientRes) throw new Error('Klienti nejsou načteni')
                setClients(clientRes.items ?? [])
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingData(false)
            }
            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    useEffect(() => {
        if (!loadingData && recalculateOperatingTimeForEachProduct) {
            recalculateOperatingTimeForEachProduct()
        }
    }, [event.startsAt, event.endsAt])

    const handleOrderSelectChange = (eventChange: SelectChangeEvent<OrderState>) => {
        const {
            target: { value },
        } = eventChange
        setEvent({ ...event, orderState: convertStringToOrderState(Number(value)) })
    }

    return (
        <CustomContainer bgColor={theme.palette.background.default} disableMarginTop>
            <Grid container rowSpacing={3} columnSpacing={3}>
                <Grid item xs={6}>
                    <Typography variant='h6' color='primary'>
                        Základní informace
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <CheckBoxContainer
                        checked={event.isEverythingReady}
                        onClick={() => setEvent({ ...event, isEverythingReady: !event.isEverythingReady })}
                        label='Vše připraveno'
                    />
                </Grid>
                <Grid item xs={6}>
                    <Stack direction='row' gap={3}>
                        {loadingData ? (
                            <CircularProgress size={40} />
                        ) : clients ? (
                            <Autocomplete
                                fullWidth
                                size='small'
                                options={clients}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Klient'
                                        InputProps={{
                                            ...params.InputProps,
                                            style: {
                                                maxHeight: '36px',
                                            },
                                        }}
                                        style={{ backgroundColor: theme.palette.customBackground.default }}
                                    />
                                )}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Typography variant='body1'>{option.name}</Typography>
                                    </li>
                                )}
                                filterOptions={(options, params) => {
                                    const filteredClients = options.filter(
                                        (client) =>
                                            convertToSearchString(client.name).includes(
                                                convertToSearchString(params.inputValue)
                                            ) ||
                                            client.contacts.some(
                                                (contact) =>
                                                    convertToSearchString(contact.phone).includes(
                                                        convertToSearchString(params.inputValue)
                                                    ) ||
                                                    convertToSearchString(contact.email).includes(
                                                        convertToSearchString(params.inputValue)
                                                    ) ||
                                                    convertToSearchString(contact.name).includes(
                                                        convertToSearchString(params.inputValue)
                                                    )
                                            )
                                    )
                                    return filteredClients
                                }}
                                defaultValue={clients.find((client) => client.id === event.clientId)}
                                onChange={(eventChange, value) => {
                                    if (!value) return
                                    setEvent({
                                        ...event,
                                        clientId: value.id,
                                        clientName: value.name,
                                    })
                                }}
                            />
                        ) : (
                            <Typography variant='body1' color='error'>
                                Klienti nejsou načteni
                            </Typography>
                        )}

                        <SmallPlusButton onClick={onAddNewClient} />
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimeRangePicker
                        dateFrom={typeof event.startsAt === 'string' ? event.startsAt : event.startsAt.toUTCString()}
                        dateTo={typeof event.endsAt === 'string' ? event.endsAt : event.endsAt.toUTCString()}
                        canBeCanceled={false}
                        onSetDateFrom={(date) => {
                            date && addHoursToEvent
                                ? setEvent({
                                      ...event,
                                      startsAt: date,
                                      endsAt:
                                          //date + addHoursToEvent hours if addHoursToEvent exists
                                          new Date(date.getTime() + addHoursToEvent * 60 * 60 * 1000),
                                  })
                                : date && setEvent({ ...event, startsAt: date })
                        }}
                        onSetDateTo={(date) => date && setEvent({ ...event, endsAt: date })}
                        labelFrom='Začátek akce'
                        labelTo='Konec akce'
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomTextField
                        fullWidth
                        variant='outlined'
                        placeholder='Název akce'
                        label='Název akce'
                        size='small'
                        value={event.name}
                        onChange={(e) => setEvent({ ...event, name: e.target.value })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <CustomTextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        placeholder='Ulice a č.p./č.o.'
                        label='Ulice a č.p./č.o.'
                        value={event.address?.street}
                        onChange={(e) => setEvent({ ...event, address: { ...event.address, street: e.target.value } })}
                    />
                </Grid>
                <Grid item xs={4}>
                    <CustomTextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        placeholder='Město'
                        label='Město'
                        value={event.address?.city}
                        onChange={(e) => setEvent({ ...event, address: { ...event.address, city: e.target.value } })}
                    />
                </Grid>
                <Grid item xs={2}>
                    <CustomTextField
                        variant='outlined'
                        size='small'
                        placeholder='PSČ'
                        label='PSČ'
                        value={event.address?.zip}
                        onChange={(e) => setEvent({ ...event, address: { ...event.address, zip: e.target.value } })}
                    />
                </Grid>
                {event.contacts?.map((contact, index) => (
                    <EventContact
                        key={index}
                        phone={contact.phone}
                        name={contact.name}
                        onDelete={() => {
                            const contacts = [...event.contacts]
                            contacts.splice(index, 1)
                            setEvent({ ...event, contacts })
                        }}
                        onChangePhone={(phone) => {
                            const contacts = [...event.contacts]
                            contacts[index].phone = phone
                            setEvent({ ...event, contacts })
                        }}
                        onChangeName={(name) => {
                            const contacts = [...event.contacts]
                            contacts[index].name = name
                            setEvent({ ...event, contacts })
                        }}
                        onAdd={() => {
                            const contacts = [...event.contacts]
                            contacts.push({ name: '', phone: '', email: '' })
                            setEvent({ ...event, contacts })
                        }}
                        isFirst={index === 0}
                    />
                ))}
                <Grid item xs={4}>
                    <PaymentSelect
                        paymentState={event.paymentState}
                        onChange={(value) =>
                            setEvent({
                                ...event,
                                paymentState: convertStringToPaymentState(Number(value)) ?? PaymentState.NotSet,
                            })
                        }
                        isInvoiced={invoice !== undefined}
                        isSmall
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControl size='small' fullWidth>
                        <InputLabel>Stav zakázky</InputLabel>
                        <Select
                            value={event.orderState}
                            onChange={handleOrderSelectChange}
                            input={
                                <OutlinedInput
                                    label='Stav zakázky'
                                    style={{ background: theme.palette.customBackground.default, maxHeight: 36 }}
                                />
                            }
                            renderValue={(selected) => convertOrderStateTypeToString(convertStringToOrderState(Number(selected)))}
                        >
                            <MenuItem value={OrderState.OfferSent}>Odeslaná</MenuItem>
                            <MenuItem value={OrderState.Asked}>Poptávka</MenuItem>
                            <MenuItem value={OrderState.Finished}>Proběhlá</MenuItem>
                            <MenuItem value={OrderState.OfferAccepted}>Schválená</MenuItem>
                            <MenuItem value={OrderState.Cancelled}>Zrušená</MenuItem>
                            <MenuItem value={OrderState.CancelledWeather}>Zrušená počasí</MenuItem>
                            <MenuItem value={OrderState.OfferRejected}>Neakceptovaná</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <CreateInvoice
                        invoice={invoice}
                        createInvoice={createInvoice}
                        onDelete={onDeleteInvoice}
                        hasContactWithEmail={event.hasContactWithEmail}
                        taxDate={event.startsAt}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='h6' color='primary' mb={2}>
                        Popis
                    </Typography>
                    <CustomTextField
                        fullWidth
                        variant='outlined'
                        placeholder='Nejčastěji e-mailová korespondence s klientem'
                        size='small'
                        rows={10}
                        multiline
                        value={event.description}
                        onChange={(e) => setEvent({ ...event, description: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='h6' color='primary' mb={2}>
                        Pozámka pro plánování
                    </Typography>
                    <CustomTextField
                        fullWidth
                        variant='outlined'
                        placeholder='Pozámka pro plánování'
                        size='small'
                        rows={2}
                        multiline
                        value={event.planningNote}
                        onChange={(e) => setEvent({ ...event, planningNote: e.target.value })}
                    />
                </Grid>
            </Grid>
        </CustomContainer>
    )
}

export default EventBasicInformation
